/**
 * Generates a Marriott reservation URL with the provided parameters
 * @param {Object} params - The reservation parameters
 * @param {number} params.numberOfRooms - Number of rooms (default: 1)
 * @param {number} params.numberOfAdults - Number of adults (default: 1)
 * @param {number} params.childrenCount - Number of children (default: 0)
 * @param {string} params.childrenAges - Ages of children, comma-separated (default: "")
 * @param {boolean} params.flexibleDateSearchRateDisplay - Flexible date search (default: false)
 * @param {boolean} params.useRewardsPoints - Use rewards points (default: false)
 * @param {string} params.marrOfferId - Marriott offer ID (required)
 * @param {string} params.fromDate - Check-in date in MM/DD/YYYY format (required)
 * @param {string} params.toDate - Check-out date in MM/DD/YYYY format (required)
 * @param {string} params.corporateCode - Corporate code (optional)
 * @param {string} params.clusterCode - Cluster code (optional)
 * @param {string} params.propertyCode - Property code (required)
 * @returns {string} The generated Marriott reservation URL
 */
export const generateMarriottReservationUrl = (params) => {
  const {
    numberOfRooms = 1,
    numberOfAdults = 1,
    childrenCount = 0,
    childrenAges = "",
    flexibleDateSearchRateDisplay = false,
    useRewardsPoints = false,
    marrOfferId,
    fromDate,
    toDate,
    corporateCode,
    clusterCode,
    propertyCode
  } = params;

  // Validate required parameters
  if (!fromDate || !toDate || !propertyCode) {
    throw new Error(
      "fromDate, toDate, and propertyCode are required parameters"
    );
  }

  // Build the URL parameters
  const urlParams = new URLSearchParams();

  // Add all parameters
  urlParams.append("numberOfRooms", numberOfRooms.toString());
  urlParams.append("numberOfAdults", numberOfAdults.toString());
  urlParams.append("childrenCount", childrenCount.toString());
  urlParams.append("childrenAges", childrenAges);
  urlParams.append(
    "flexibleDateSearchRateDisplay",
    flexibleDateSearchRateDisplay.toString()
  );
  urlParams.append("useRewardsPoints", useRewardsPoints.toString());
  urlParams.append("marrOfferId", marrOfferId);
  urlParams.append("fromDate", fromDate);
  urlParams.append("toDate", toDate);
  urlParams.append("propertyCode", propertyCode);

  // Add optional parameters if provided
  if (corporateCode) {
    urlParams.append("corporateCode", corporateCode);
  }
  if (clusterCode) {
    urlParams.append("clusterCode", clusterCode);
  }

  // Construct the final URL
  const baseUrl = "https://www.marriott.com/reservation/availabilitySearch.mi";
  return `${baseUrl}?&${urlParams.toString()}`;
};